// src/Leagues.js
import React, { useState, useEffect, useMemo } from "react";
import { supabase } from "./supabaseClient";
import toast from "react-hot-toast";

const containerStyle = {
  padding: "1rem",
  fontFamily: "Arial, sans-serif",
};

const cardStyle = {
  background: "#f9fafb",
  padding: "1rem",
  borderRadius: "8px",
  border: "1px solid #e5e7eb",
  marginBottom: "1.5rem",
};

const inputStyle = {
  padding: "8px 12px",
  marginRight: "10px",
  borderRadius: "4px",
  border: "1px solid #ccc",
  fontSize: "1rem",
};

const buttonStyle = {
  padding: "8px 16px",
  backgroundColor: "#2563eb",
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  fontWeight: "bold",
};

const tableStyle = {
  borderCollapse: "collapse",
  width: "100%",
  marginTop: "1rem",
};

const thTdStyle = {
  border: "1px solid #ccc",
  padding: "8px 12px",
  textAlign: "left",
};

const thStyle = {
  backgroundColor: "#f4f4f4",
  fontWeight: "bold",
};

const Leagues = ({ teams = [] }) => {
  const [leagues, setLeagues] = useState([]);
  const [leagueNameInput, setLeagueNameInput] = useState("");
  const [inviteCodeInput, setInviteCodeInput] = useState("");
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [leagueMembersData, setLeagueMembersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isJoining, setIsJoining] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Helper to extract invite code across field naming variations
  const getLeagueCode = (league) => {
    if (!league) return "";
    return league.code || league.invite_code || league.inviteCode || "";
  };

  // Get logged-in user and listen for auth state changes
  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setCurrentUser(user);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange((_event, session) => {
      setCurrentUser(session?.user || null);
    });

    return () => {
      authListener?.subscription.unsubscribe();
    };
  }, []);

  // Build price map directly from teams prop (0 database reads)
  const priceMap = useMemo(() => {
    return teams.reduce((acc, team) => {
      acc[team.id] = team.price || 0;
      return acc;
    }, {});
  }, [teams]);

  // Subscribe to leagues where current user is a member
  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    const fetchLeagues = async () => {
      try {
        // Query leagues where the members array contains currentUser.id
        const { data, error } = await supabase
          .from("leagues")
          .select("*")
          .contains("members", [currentUser.id]);

        if (error) throw error;

        setLeagues(data || []);

        // Keep selectedLeague updated with latest data
        setSelectedLeague((prevSelected) => {
          if (!prevSelected) return null;
          return (data || []).find((l) => l.id === prevSelected.id) || prevSelected;
        });
      } catch (err) {
        console.error("Error fetching leagues:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchLeagues();

    const channel = supabase
      .channel("public:leagues")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "leagues" },
        () => {
          fetchLeagues();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUser]);

  // Fetch league member profiles when selectedLeague or priceMap changes
  useEffect(() => {
    const fetchLeagueLeaderboard = async () => {
      if (!selectedLeague || !selectedLeague.members?.length) {
        setLeagueMembersData([]);
        return;
      }

      setLoadingMembers(true);
      try {
        // Fetch profiles for all member IDs in one request
        const { data: profiles, error } = await supabase
          .from("profiles")
          .select("*")
          .in("id", selectedLeague.members);

        if (error) throw error;

        const memberProfiles = (profiles || []).map((data) => {
          const cash = typeof data.cash === "number" ? data.cash : 0;
          const portfolio = data.portfolio || {};

          const portfolioValue = Object.entries(portfolio).reduce(
            (acc, [teamId, shares]) =>
              acc + (Number(shares) || 0) * (priceMap[teamId] || 0),
            0
          );

          return {
            uid: data.id,
            displayName:
              data.username || data.displayName || data.email || "Player",
            cash,
            portfolioValue,
            netWorth: cash + portfolioValue,
          };
        });

        memberProfiles.sort((a, b) => b.netWorth - a.netWorth);
        setLeagueMembersData(memberProfiles);
      } catch (error) {
        console.error("Error fetching league leaderboard:", error);
      } finally {
        setLoadingMembers(false);
      }
    };

    fetchLeagueLeaderboard();
  }, [selectedLeague, priceMap]);

  // Create a new league
  const handleCreateLeague = async (e) => {
    e.preventDefault();
    const cleanName = leagueNameInput.trim();
    if (!cleanName) return toast.error("Enter a league name.");
    if (!currentUser) return toast.error("You must be logged in.");

    setIsCreating(true);
    const generatedCode = Math.random().toString(36).substring(2, 8).toUpperCase();

    try {
      const { error } = await supabase.from("leagues").insert([
        {
          name: cleanName,
          code: generatedCode,
          invite_code: generatedCode,
          creator_id: currentUser.id,
          members: [currentUser.id],
        },
      ]);

      if (error) throw error;

      setLeagueNameInput("");
      toast.success(`League created! Code: ${generatedCode}`, { duration: 5000 });
    } catch (error) {
      console.error("Error creating league:", error);
      toast.error(`Error creating league: ${error.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  // Join an existing league
  const handleJoinLeague = async (e) => {
    e.preventDefault();
    const cleanCode = inviteCodeInput.trim().toUpperCase();
    if (!cleanCode) return toast.error("Enter a valid invite code.");
    if (!currentUser) return toast.error("You must be logged in.");

    setIsJoining(true);

    try {
      // Find league matching code, invite_code, or id
      // Find league matching code or invite_code only (avoiding UUID type collision on 'id')
      const { data: leaguesFound, error: fetchError } = await supabase
        .from("leagues")
        .select("*")
        .or(`code.eq.${cleanCode},invite_code.eq.${cleanCode}`);

      if (fetchError) throw fetchError;

      if (!leaguesFound || leaguesFound.length === 0) {
        toast.error("No league found with that invite code.");
        return;
      }

      const leagueDoc = leaguesFound[0];
      const membersList = leagueDoc.members || [];

      if (membersList.includes(currentUser.id)) {
        toast.error("You are already a member of this league!");
        return;
      }

      const updatedMembers = [...membersList, currentUser.id];

      const { error: updateError } = await supabase
        .from("leagues")
        .update({ members: updatedMembers })
        .eq("id", leagueDoc.id);

      if (updateError) throw updateError;

      setInviteCodeInput("");
      toast.success(`Successfully joined ${leagueDoc.name || "League"}!`);
    } catch (error) {
      console.error("Error joining league:", error);
      toast.error(`Error joining league: ${error.message}`);
    } finally {
      setIsJoining(false);
    }
  };

  // Copy code utility
  const copyInviteCode = (code) => {
    if (!code) return;
    navigator.clipboard.writeText(code);
    toast.success("Invite code copied to clipboard!");
  };

  if (loading) {
    return <div style={containerStyle}>Loading leagues...</div>;
  }

  const currentInviteCode = getLeagueCode(selectedLeague);

  return (
    <div style={containerStyle}>
      <h2>Private Leagues</h2>

      {selectedLeague ? (
        <div>
          <button
            onClick={() => setSelectedLeague(null)}
            style={{
              ...buttonStyle,
              backgroundColor: "#6b7280",
              marginBottom: "1rem",
            }}
          >
            ← Back to My Leagues
          </button>

          <div style={cardStyle}>
            <h3 style={{ margin: "0 0 10px 0" }}>{selectedLeague.name}</h3>
            <p style={{ margin: 0 }}>
              Invite Code:{" "}
              <strong
                onClick={() => copyInviteCode(currentInviteCode)}
                title="Click to copy"
                style={{
                  background: "#e5e7eb",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  cursor: "pointer",
                  userSelect: "all",
                  letterSpacing: "1px",
                }}
              >
                {currentInviteCode} 📋
              </strong>
            </p>
          </div>

          <h3>League Leaderboard</h3>
          {loadingMembers ? (
            <p style={{ color: "#6b7280" }}>Loading leaderboard...</p>
          ) : (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={{ ...thTdStyle, ...thStyle }}>Rank</th>
                  <th style={{ ...thTdStyle, ...thStyle }}>Player</th>
                  <th style={{ ...thTdStyle, ...thStyle }}>Cash</th>
                  <th style={{ ...thTdStyle, ...thStyle }}>Portfolio Value</th>
                  <th style={{ ...thTdStyle, ...thStyle }}>Total Net Worth</th>
                </tr>
              </thead>
              <tbody>
                {leagueMembersData.map((member, index) => (
                  <tr
                    key={member.uid}
                    style={{
                      backgroundColor:
                        member.uid === currentUser?.id
                          ? "#f0fdf4"
                          : "transparent",
                    }}
                  >
                    <td style={thTdStyle}>#{index + 1}</td>
                    <td style={thTdStyle}>
                      {member.displayName}{" "}
                      {member.uid === currentUser?.id && "(You)"}
                    </td>
                    <td style={thTdStyle}>${member.cash.toFixed(2)}</td>
                    <td style={thTdStyle}>${member.portfolioValue.toFixed(2)}</td>
                    <td
                      style={{
                        ...thTdStyle,
                        fontWeight: "bold",
                        color: "#2b8a3e",
                      }}
                    >
                      ${member.netWorth.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "1.5rem",
              marginBottom: "2rem",
            }}
          >
            <div style={cardStyle}>
              <h3 style={{ marginTop: 0 }}>Create a League</h3>
              <form onSubmit={handleCreateLeague}>
                <input
                  type="text"
                  placeholder="League Name"
                  value={leagueNameInput}
                  onChange={(e) => setLeagueNameInput(e.target.value)}
                  disabled={isCreating}
                  style={inputStyle}
                />
                <button
                  type="submit"
                  disabled={isCreating}
                  style={{
                    ...buttonStyle,
                    opacity: isCreating ? 0.6 : 1,
                  }}
                >
                  {isCreating ? "Creating..." : "Create"}
                </button>
              </form>
            </div>

            <div style={cardStyle}>
              <h3 style={{ marginTop: 0 }}>Join a League</h3>
              <form onSubmit={handleJoinLeague}>
                <input
                  type="text"
                  placeholder="Invite Code"
                  value={inviteCodeInput}
                  onChange={(e) => setInviteCodeInput(e.target.value)}
                  disabled={isJoining}
                  style={{ ...inputStyle, textTransform: "uppercase" }}
                />
                <button
                  type="submit"
                  disabled={isJoining}
                  style={{
                    ...buttonStyle,
                    backgroundColor: "#2b8a3e",
                    opacity: isJoining ? 0.6 : 1,
                  }}
                >
                  {isJoining ? "Joining..." : "Join"}
                </button>
              </form>
            </div>
          </div>

          <h3>My Leagues</h3>
          {leagues.length === 0 ? (
            <p style={{ color: "#6b7280" }}>
              You aren't in any private leagues yet. Create one or join one above!
            </p>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
                gap: "1rem",
              }}
            >
              {leagues.map((league) => (
                <div
                  key={league.id}
                  style={{
                    ...cardStyle,
                    cursor: "pointer",
                    transition: "transform 0.1s",
                  }}
                  onClick={() => setSelectedLeague(league)}
                >
                  <h4 style={{ margin: "0 0 10px 0" }}>{league.name}</h4>
                  <p
                    style={{
                      margin: "0 0 5px 0",
                      color: "#4b5563",
                      fontSize: "0.9rem",
                    }}
                  >
                    Members: {league.members?.length || 0}
                  </p>
                  <p
                    style={{
                      margin: 0,
                      color: "#2563eb",
                      fontSize: "0.9rem",
                      fontWeight: "bold",
                    }}
                  >
                    View Leaderboard →
                  </p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Leagues;